import { Component, For } from 'solid-js';

export interface ButtonGroupOption<T extends string = string> {
  value: T;
  label: string;
}

export interface ButtonGroupProps<T extends string = string> {
  options: ButtonGroupOption<T>[];
  value: T;
  onChange(value: T): void;
  testId?: string;
  disabled?: boolean;
}

export const ButtonGroup: Component<ButtonGroupProps<string>> = (props) => {
  const baseButtonClasses =
    'flex-1 px-3 py-2 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1';

  const selectedClasses = 'bg-blue-pale text-blue border border-blue';
  const unselectedClasses =
    'bg-white text-base-content border border-gray-3 hover:bg-gray-light';
  const selectedDisabledClasses =
    'bg-gray-1 text-gray-8 border border-gray-5 cursor-default';
  const unselectedDisabledClasses =
    'bg-gray-0 text-gray-6 border border-gray-3 cursor-default';

  const getButtonClasses = (
    isSelected: boolean,
    index: number,
    total: number
  ): string => {
    const positionClasses =
      index === 0
        ? 'rounded-l-sm'
        : index === total - 1
          ? 'rounded-r-sm -ml-px'
          : '-ml-px';

    const stateClasses = props.disabled
      ? isSelected
        ? selectedDisabledClasses
        : unselectedDisabledClasses
      : isSelected
        ? selectedClasses
        : unselectedClasses;

    return `${baseButtonClasses} ${positionClasses} ${stateClasses}`;
  };

  return (
    <div
      class="flex w-full"
      role="group"
      data-testid={props.testId ?? 'button-group'}
    >
      <For each={props.options}>
        {(option, index) => (
          <button
            type="button"
            class={getButtonClasses(
              props.value === option.value,
              index(),
              props.options.length
            )}
            onClick={() => props.onChange(option.value)}
            disabled={props.disabled}
            data-testid={`${props.testId ?? 'button-group'}-${option.value}`}
            aria-pressed={props.value === option.value}
          >
            {option.label}
          </button>
        )}
      </For>
    </div>
  );
};
